import { Dispatch, Action } from "redux";
import { ThunkDispatch } from "redux-thunk";

import { loadAndTrack } from "redux-request-loading";
import * as moment from "moment";

import { Payload } from "@neworbit/redux-helpers";

import { toast } from "@teamprevent/common/toasts";
import { CaseType } from "@teamprevent/common/model";
import { TaskModel } from "@teamprevent/common/task/taskActions/models";
import { AppointmentAndParent } from "@teamprevent/common/calendar/models";
import { ClinicLocationTypeName } from "@teamprevent/common/appointment/models";

import { TaskType } from "../task/model";
import { ApplicationState } from "../applicationState";

import { BookAppointmentsApi } from "./bookAppointmentsApi";
import {
    BookAppointmentTaskListModel,
    BookIntoClinicPayload,
    AdHocCalendarAppointmentModel,
    BookAppointmentErrors,
    BookIntoClinicSuccessResult,
    BookAdHocAppointmentSuccessResult,
    RebookAppointmentSuccessResult,
    AddBookingNotesSuccessResult,
    BookAdHocClinicAppointmentSuccessResult,
    BookAppointmentTaskListModelData,
    RebookingReasonModel
} from "./models";
import * as actions from "./actionTypes";

import { push } from "redux-little-router";

export type LoadBookAppointmentTasksSuccess = ({ type: typeof actions.LOAD_BOOK_APPOINTMENT_TASKS_SUCCESS }) & Payload<BookAppointmentTaskListModel[]>;
export type LoadBookAppointmentTaskListModelData = ({ type: typeof actions.LOAD_BOOK_APPOINTMENT_TASKS_DATA }) & Payload<BookAppointmentTaskListModelData>;
export type LoadBookAppointmentTaskTypesSuccess = ({ type: typeof actions.LOAD_BOOK_APPOINTMENT_TASK_TYPES_SUCCESS }) & Payload<TaskType[]>;
export type BookIntoClinicStarted = ({ type: typeof actions.BOOK_INTO_CLINIC_STARTED }) & Payload<BookIntoClinicPayload>;
export type BookIntoClinicError = ({ type: typeof actions.BOOK_INTO_CLINIC_ERROR }) & Payload<BookIntoClinicPayload>;
export type BookIntoClinicSuccess = ({ type: typeof actions.BOOK_INTO_CLINIC_SUCCESS }) & Payload<BookIntoClinicSuccessResult>;
export type BookAdHocAppointmentStarted = ({ type: typeof actions.BOOK_AD_HOC_APPOINTMENT_STARTED }) & Payload<void>;
export type BookAdHocAppointmentSuccess = ({ type: typeof actions.BOOK_AD_HOC_APPOINTMENT_SUCCESS }) & Payload<BookAdHocAppointmentSuccessResult>;
export type BookAdHocClinicAppointmentSuccess =
    ({ type: typeof actions.BOOK_AD_HOC_CLINIC_APPOINTMENT_SUCCESS }) & Payload<BookAdHocClinicAppointmentSuccessResult>;
export type BookAdHocAppointmentError = ({ type: typeof actions.BOOK_AD_HOC_APPOINTMENT_ERROR }) & Payload<BookAppointmentErrors>;
export type RebookAppointmentSuccess = ({ type: typeof actions.REBOOK_APPOINTMENT_SUCCESS }) & Payload<RebookAppointmentSuccessResult>;
export type ShowOnlyClinicsWithInsufficientCliniciansChanged =
    ({ type: typeof actions.SHOW_ONLY_CLINICS_WITH_INSUFFICIENT_CLINICIANS_CHANGED }) & Payload<boolean>;
export type ClinicTypeChanged = ({ type: typeof actions.CLINIC_TYPE_CHANGED }) & Payload<string>;
export type ClinicLocationTypeChanged = ({ type: typeof actions.CLINIC_LOCATION_TYPE_CHANGED }) & Payload<ClinicLocationTypeName>;
export type DateChanged = ({ type: typeof actions.DATE_CHANGED }) & Payload<moment.Moment>;
export type SelectedUserIdsChanged = ({ type: typeof actions.SELECTED_USER_IDS_CHANGED }) & Payload<string[]>;
export type SelectedRoomIdsChanged = ({ type: typeof actions.SELECTED_ROOM_IDS_CHANGED }) & Payload<string[]>;
export type ChooseAndBookSuccess = ({ type: typeof actions.REQUEST_CHOOSE_AND_BOOK_SUCCESS }) & Payload<string>;
export type AddBookingNotesSuccess = ({ type: typeof actions.ADD_BOOKING_NOTES_SUCCESS }) & Payload<AddBookingNotesSuccessResult>;

export type BookAppointmentAction =
    LoadBookAppointmentTasksSuccess |
    LoadBookAppointmentTaskListModelData |
    BookIntoClinicStarted |
    BookIntoClinicError |
    BookIntoClinicSuccess |
    LoadBookAppointmentTaskTypesSuccess |
    BookAdHocAppointmentStarted |
    BookAdHocAppointmentSuccess |
    BookAdHocAppointmentError |
    RebookAppointmentSuccess |
    ShowOnlyClinicsWithInsufficientCliniciansChanged |
    ClinicTypeChanged |
    ClinicLocationTypeChanged |
    DateChanged |
    SelectedUserIdsChanged |
    SelectedRoomIdsChanged |
    ChooseAndBookSuccess |
    AddBookingNotesSuccess |
    BookAdHocClinicAppointmentSuccess;

export const loadBookAppointmentTasksSuccess = (payload: BookAppointmentTaskListModel[]) => ({
    payload,
    type: actions.LOAD_BOOK_APPOINTMENT_TASKS_SUCCESS
});

export const loadBookAppointmentTaskListModelData = (payload: BookAppointmentTaskListModelData) => ({
    payload,
    type: actions.LOAD_BOOK_APPOINTMENT_TASKS_DATA
});

export const loadBookAppointmentTaskTypesSuccess = (payload: TaskType[]) => ({
    payload,
    type: actions.LOAD_BOOK_APPOINTMENT_TASK_TYPES_SUCCESS
});

export const bookIntoClinicStarted = (payload: BookIntoClinicPayload): BookIntoClinicStarted => ({
    payload,
    type: actions.BOOK_INTO_CLINIC_STARTED
});

export const bookIntoClinicError = (payload: BookIntoClinicPayload) => ({
    payload,
    type: actions.BOOK_INTO_CLINIC_ERROR
});

export const bookAdHocAppointmentStarted = () => ({ type: actions.BOOK_AD_HOC_APPOINTMENT_STARTED });

export const bookAdHocAppointmentSuccess = (payload: BookAdHocAppointmentSuccessResult): BookAdHocAppointmentSuccess => ({
    payload,
    type: actions.BOOK_AD_HOC_APPOINTMENT_SUCCESS
});

export const bookAdHocClinicAppointmentSuccess = (payload: BookAdHocClinicAppointmentSuccessResult): BookAdHocClinicAppointmentSuccess => ({
    payload,
    type: actions.BOOK_AD_HOC_CLINIC_APPOINTMENT_SUCCESS
});

export const bookAdHocAppointmentError = (payload: BookAppointmentErrors) => ({
    payload,
    type: actions.BOOK_AD_HOC_APPOINTMENT_ERROR
});

export const bookIntoAppointmentSuccess = (payload: BookIntoClinicSuccessResult) => ({
    payload,
    type: actions.BOOK_INTO_CLINIC_SUCCESS
});

export const rebookAppointmentSuccess = (payload: RebookAppointmentSuccessResult): RebookAppointmentSuccess => ({
    payload,
    type: actions.REBOOK_APPOINTMENT_SUCCESS
});

export const chooseAndBookSuccess = (payload: string): ChooseAndBookSuccess => ({
    payload,
    type: actions.REQUEST_CHOOSE_AND_BOOK_SUCCESS
});

export const addBookingNotesSuccess = (payload: AddBookingNotesSuccessResult): AddBookingNotesSuccess => ({
    payload,
    type: actions.ADD_BOOKING_NOTES_SUCCESS
});

export const showOnlyClinicsWithInsufficientCliniciansChanged = (payload: boolean): ShowOnlyClinicsWithInsufficientCliniciansChanged => ({
    payload,
    type: actions.SHOW_ONLY_CLINICS_WITH_INSUFFICIENT_CLINICIANS_CHANGED
});

export const clinicTypeChanged = (payload: string): ClinicTypeChanged => ({
    payload,
    type: actions.CLINIC_TYPE_CHANGED
});

export const clinicLocationTypeChanged = (payload: ClinicLocationTypeName): ClinicLocationTypeChanged => ({
    payload,
    type: actions.CLINIC_LOCATION_TYPE_CHANGED
});

export const focusDateChanged = (payload: moment.Moment): DateChanged => ({
    payload,
    type: actions.DATE_CHANGED
});

export const selectedUserIdsChanged = (payload: string[]): SelectedUserIdsChanged => ({
    payload,
    type: actions.SELECTED_USER_IDS_CHANGED
});

export const selectedRoomIdsChanged = (payload: string[]): SelectedRoomIdsChanged => ({
    payload,
    type: actions.SELECTED_ROOM_IDS_CHANGED
});

export const loadBookAppointmentTasks = (customerIds: string[], taskTypes: string[], caseTypes: CaseType[], taskId: string, teamIds: string[]) => {
    return async (dispatch: Dispatch) => {
        const result = await loadAndTrack(
            dispatch, actions.LOAD_BOOK_APPOINTMENT_TASKS,
            new BookAppointmentsApi().get(customerIds, taskTypes, caseTypes, taskId, teamIds));
        dispatch(loadBookAppointmentTasksSuccess(result.appointmentTasks));
        dispatch(loadBookAppointmentTaskListModelData(result.data));
    };
};

export const clearBookAppointmentTasks = () => {
    return (dispatch: Dispatch) => {
        dispatch(loadBookAppointmentTasksSuccess([]));
        dispatch(loadBookAppointmentTaskListModelData({ count: 0 }));
    };
};

export const loadBookAppointmentTaskTypes = () => {
    return async (dispatch: Dispatch) => {
        const result = await loadAndTrack(dispatch, actions.LOAD_BOOK_APPOINTMENT_TASK_TYPES, new BookAppointmentsApi().getBookTaskTypes());
        dispatch(loadBookAppointmentTaskTypesSuccess(result));
    };
};

export const bookIntoClinic = (
    bookTask: BookAppointmentTaskListModel,
    appointment: Pick<AppointmentAndParent, "start" | "end" | "description" | "parentAppointment">,
    locationType?: ClinicLocationTypeName
) => {
    return async (dispatch: Dispatch) => {
        if (appointment.start.isBefore(moment().utc(true))) {
            toast.error("Appointments cannot be booked in the past");
            return;
        }
        dispatch(bookIntoClinicStarted({ bookTask, appointment }));
        try {
            const result = await loadAndTrack(
                dispatch,
                actions.BOOK_INTO_CLINIC,
                new BookAppointmentsApi().bookIntoClinic(
                    appointment.parentAppointment.id,
                    bookTask.ohCaseId,
                    bookTask.id,
                    appointment.start,
                    appointment.end,
                    appointment.description,
                    locationType
                ));
            if (result.success) {
                toast.success("Appointment booked successfully");
                dispatch(bookIntoAppointmentSuccess(result));
                dispatch(push("/book-appointments"));
            } else {
                dispatch(bookIntoClinicError({ bookTask, appointment }));
                toast.error("Appointment booking failed");
            }
        } catch {
            dispatch(bookIntoClinicError({ bookTask, appointment }));
            toast.error("Appointment booking failed");
        }
    };
};

export const bookIntoMobileClinic = (bookTask: BookAppointmentTaskListModel, appointment: AppointmentAndParent) => {
    return async (dispatch: Dispatch) => {
        if (appointment.start.isBefore(moment().utc(true))) {
            toast.error("Appointments cannot be booked in the past");
            return;
        }
        dispatch(bookIntoClinicStarted({ bookTask, appointment }));
        try {
            const result = await loadAndTrack(
                dispatch,
                actions.BOOK_INTO_CLINIC,
                new BookAppointmentsApi().bookIntoMobileClinic(
                    appointment.id,
                    bookTask.ohCaseId,
                    bookTask.id
                ));
            if (result.success) {
                toast.success("Appointment booked successfully");
                dispatch(bookIntoAppointmentSuccess(result));
            } else {
                dispatch(bookIntoClinicError({ bookTask, appointment }));
                toast.error("Appointment booking failed");
            }
        } catch {
            dispatch(bookIntoClinicError({ bookTask, appointment }));
            toast.error("Appointment booking failed");
        }
    };
};

export const bookAdHocAppointment = (bookTask: BookAppointmentTaskListModel, appointment: AdHocCalendarAppointmentModel) => {
    return async (dispatch: Dispatch) => {
        dispatch(bookAdHocAppointmentStarted());
        try {
            const result = await loadAndTrack(
                dispatch,
                actions.BOOK_AD_HOC_APPOINTMENT,
                new BookAppointmentsApi().bookAdHocAppointment(bookTask.ohCaseId, bookTask.id, appointment));
            if (result.success) {
                dispatch(bookAdHocAppointmentSuccess({ bookTask, ohCaseId: bookTask.ohCaseId, appointment: result.appointment, tasks: result.updatedTasks }));
                dispatch(push("/book-appointments"));
                toast.success("Appointment booked successfully");
            } else {
                dispatch(bookAdHocAppointmentError(result.errors));
            }
        } catch {
            toast.error("Appointment booking failed");
        }
    };
};

export const bookAdHocClinicAppointment = (clinicId: string, appointment: AdHocCalendarAppointmentModel) => {
    return async (dispatch: Dispatch) => {
        const updatedClinic = await loadAndTrack(
            dispatch,
            actions.BOOK_AD_HOC_CLINIC_APPOINTMENT,
            new BookAppointmentsApi().bookAdHocClinicAppointment(clinicId, appointment)
        );
        dispatch(bookAdHocClinicAppointmentSuccess({ appointment: updatedClinic }));
    };
};

export const rebookAppointment = (appointmentTask: TaskModel, rebookingReason: RebookingReasonModel, preventNavigation?: boolean) => {
    return async (dispatch: Dispatch) => {
        try {
            const apiResult = await loadAndTrack(
                dispatch,
                `${actions.REBOOK_APPOINTMENT}_${appointmentTask.id}`,
                new BookAppointmentsApi().rebookAppointment(appointmentTask, rebookingReason)
            );

            dispatch(rebookAppointmentSuccess({ ...apiResult, ohCaseId: appointmentTask.caseId }));

            if (!preventNavigation) {
                // It's been observed that sometimes when redirecting to this url here via the usual dispatch push method
                // it does not correctly work without a full reload so that's what we're going to do because I can't find the true cause
                window.location.href = `/book-appointments?bookTaskId=${apiResult.tasks.reverse()[0].id}`;
            }
        } catch (ex) {
            toast.error("Cannot rebook this appointment please check that it has not already been rebooked");
        }
    };
};

export const rebookAppointments = (appointmentTasks: TaskModel[], rebookingReason: RebookingReasonModel) => {
    return async (dispatch: Dispatch) => {
        try {
            const apiResult = await loadAndTrack(
                dispatch,
                `${actions.REBOOK_APPOINTMENT}`,
                new BookAppointmentsApi().rebookAppointments(appointmentTasks, rebookingReason)
            );

            apiResult.map(result => dispatch(rebookAppointmentSuccess({ ...result, ohCaseId: result.ohCaseId })));

            dispatch(push({ pathname: "/book-appointments", query: { bookTaskId: apiResult[0].tasks.reverse()[0].id } }));

        } catch (ex) {
            toast.error("Cannot rebook all appointments please refresh the modal and try again");
        }
    };
};

export const chooseAndBook = (task: BookAppointmentTaskListModel) => {
    return async (dispatch: ThunkDispatch<ApplicationState, undefined, any>) => {
        await loadAndTrack(
            dispatch,
            actions.REQUEST_CHOOSE_AND_BOOK,
            new BookAppointmentsApi().chooseAndBook(task.ohCaseId, task.id)
        );

        dispatch(chooseAndBookSuccess(task.id));
    };
};

export const addBookingNotes = (ohCaseId: string, taskId: string, notes: string) => {
    return async (dispatch: ThunkDispatch<ApplicationState, undefined, Action>) => {
        const updatedNotes = await loadAndTrack(
            dispatch,
            actions.ADD_BOOKING_NOTES,
            new BookAppointmentsApi().addBookingNotes(ohCaseId, taskId, notes)
        );

        dispatch(addBookingNotesSuccess({ ohCaseId, taskId, notes: updatedNotes }));
    };
};
